import { createAsyncThunk } from '@reduxjs/toolkit'
import apiRequest from "../../lib/http"
import { safeGetItem, USER } from "../../lib/storage"
import { normalizeError } from "../../utils/errorHelpers"

// The backend gates GET /contact-groups/dropdown/info to these roles (hasRoles).
// Other roles (affiliate, affiliateGroupsAdmin) get a 403 — but ~8 shared containers
// call this thunk on mount for EVERY role, which flooded Sentry with 4343x
// "User role is not valid for this action" (VLG-API-H) and left their filters
// silently blank. Skip the request for roles that can't use it. NOTE: supervisor is a
// subRole of 'user', so supervisors stay allowed here, matching the backend gate.
const DROPDOWN_INFO_ROLES = ['customer', 'user']

const currentUserRole = () => {
  try {
    return JSON.parse(safeGetItem(USER) || 'null')?.role
  } catch (_) {
    return undefined
  }
}

export const fetchContactGroups = createAsyncThunk('contact-groups/fetchContactGroups', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get('/contact-groups')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch contact-groups'
    return rejectWithValue(errorMessage)
  }
})

export const fetchContactGroupsDropdownInfo = createAsyncThunk('contact-groups/fetchContactGroupsDropdownInfo', async (_, { rejectWithValue }) => {
  try {
    // Don't fire a request we know will 403 for this role (kills the VLG-API-H flood).
    // Resolve the same shape the success path returns so consumers (result.payload.data)
    // render an empty list, not a crash.
    if (!DROPDOWN_INFO_ROLES.includes(currentUserRole())) {
      return { data: [] }
    }
    const response = await apiRequest.get('/contact-groups/dropdown/info')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch contact-groups'
    return rejectWithValue(errorMessage)
  }
})

export const getContactGroupsForFilterUtils = createAsyncThunk('contact-groups/getContactGroupsForFilterUtils', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get('/contact-groups/filters/utils')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch contact-groups'
    return rejectWithValue(errorMessage)
  }
})

export const createNewContactGroup = createAsyncThunk(
  'contact-groups/createContactGroup',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`/contact-groups`, userData)
      return response.data
    } catch (error) {
      const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to create affiliate'
      return rejectWithValue(errorMessage)
    }
  }
)

export const updateContactGroup = createAsyncThunk('contact-groups/updateContactGroup', async ({
                                                                                                 userData, groupId
                                                                                               }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/contact-groups/${groupId}`, userData)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to update affiliate'
    return rejectWithValue(errorMessage)
  }
})

export const getContactGroupById = createAsyncThunk('contact-groups/getContactGroupById', async ({ groupId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/contact-groups/${groupId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch affiliate'
    return rejectWithValue(errorMessage)
  }
})

export const getContactGroupCustomSettings = createAsyncThunk('contact-groups/getContactGroupCustomSettings', async ({ groupId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/contact-groups/${groupId}/custom-settings`)
    return response.data
  } catch (error) {
    return rejectWithValue('Failed to fetch contact group settings')
  }
})

export const removeContactGroup = createAsyncThunk('contact-groups/removeContactGroup', async (groupId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.delete(`/contact-groups/${groupId}`)
    return response.data
  } catch (error) {
    const firstError = error.response?.data?.error?.errors?.[0]
    return rejectWithValue({ message: firstError?.message || 'Failed to delete contact group', code: firstError?.code || null })
  }
})

export const getContactGroupAffiliates = createAsyncThunk('contact-groups/getContactGroupAffiliates', async (groupId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/affiliates/dropdown/info/contact-group/${groupId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch contact group affiliates'
    return rejectWithValue(errorMessage)
  }
})

export const fetchLsaSetOptions = createAsyncThunk('contact-groups/fetchLsaSetOptions', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get('/contact-groups/lsa-sets/options')
    const sets = response.data.data.sets
    return Array.isArray(sets) ? sets : []
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})
